import { ELightGalleryType } from '@quesmed/types/models';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { assetFieldMapping, type IAssetSchema } from '@/config';
import { AssetEditor } from '@/core/molecules';
import { useAsset } from '@/store/asset';
import { useAssetActions } from './use-asset-actions';

type ISchema = { assets: IAssetSchema[]; [x: string]: unknown };
type IUploadAssetData = { file: File; name: string; sensitive: boolean; caption?: string | null };

export const RecordAssetEditor = ({ type, isDisabled }: { type: 'job' | 'normal'; isDisabled?: boolean }) => {
  const assetEditor = useAsset.use.assetEditor();
  const setAssetMapping = useAsset.use.setAssetMapping();
  const closeAssetEditor = useAsset.use.closeAssetEditor();
  const { setValue, getValues } = useFormContext<ISchema>();
  const { createJobAsset, createAsset, createAssetsLoading } = useAssetActions();

  const createAssetForJob = async (
    result: IUploadAssetData,
    field: keyof IAssetSchema,
    index: number,
    latestAssets: IAssetSchema[],
  ) => {
    const { data } = await createJobAsset(
      [result],
      assetEditor?.key ?? ELightGalleryType.QUESTION,
      index,
      assetEditor?.choiceLabel ?? null,
      // getValues('referenceId') ?? null,
    );
    const imageText = `[lightgallery${index ? index : ''}]`;
    if (data?.admin?.createJobAssets) {
      const formActions = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
      // merge new assets with latest assets and sort by type and index
      const newAssets = [
        ...latestAssets,
        ...data.admin.createJobAssets.map((asset) => ({
          id: asset.id,
          index: asset.index,
          deleted: false,
          linked: false,
          choiceLabel: asset.choiceLabel,
          jobId: asset.jobId,
          type: asset.type,
          recordId: asset.recordId,
          createdAt: asset.createdAt,
          picture: asset.picture,
        })),
      ].sort((a, b) => {
        if (a.type > b.type) return 1;
        if (a.type < b.type) return -1;
        // if types are the same, sort by index
        return (a.index ?? 0) - (b.index ?? 0);
      });
      setValue('assets', newAssets as IAssetSchema[], formActions);
      setValue(field, `${getValues(field) ?? ''}\n${imageText}`, formActions);
      setAssetMapping(newAssets.filter((a) => !a.deleted) as IAssetSchema[]);
      closeAssetEditor();
    }
  };

  const createAssetForNormal = async (
    result: IUploadAssetData,
    field: keyof IAssetSchema,
    index: number,
    latestAssets: IAssetSchema[],
  ) => {
    const { data } = await createAsset(
      [result],
      // assetEditor?.key ?? ELightGalleryType.QUESTION,
      // index,
      // assetEditor?.choiceLabel ?? null,
      // getValues('referenceId') ?? null,
    );
    const imageText = `[lightgallery${index ? index : ''}]`;
    if (data?.admin?.createAssets) {
      const formActions = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
      // merge new assets with latest assets and sort by type and index
      const newAssets = [
        ...latestAssets,
        ...data.admin.createAssets.map((asset) => ({
          id: asset.id.toString(),
          index,
          deleted: false,
          linked: false,
          choiceLabel: assetEditor?.choiceLabel ?? null,
          jobId: '',
          type: assetEditor?.key ?? ELightGalleryType.EXPLANATION,
          recordId: '',
          createdAt: asset.createdAt || Date.now(),
          picture: asset,
        })),
      ].sort((a, b) => {
        if (a.type > b.type) return 1;
        if (a.type < b.type) return -1;
        // if types are the same, sort by index
        return (a.index ?? 0) - (b.index ?? 0);
      });
      setValue('assets', newAssets as IAssetSchema[], formActions);
      setValue(field, `${getValues(field) ?? ''}\n${imageText}`, formActions);
      setAssetMapping(newAssets.filter((a) => !a.deleted) as IAssetSchema[]);
      closeAssetEditor();
    }
  };

  const handleSave = useCallback(
    async (result: IUploadAssetData) => {
      if (!assetEditor) {
        return Promise.resolve();
      }
      const latestAssets = getValues('assets') ?? [];
      const formAssets = latestAssets.filter((a) => !a.deleted);
      const { key, choiceLabel } = assetEditor;
      const assets =
        formAssets.filter((asset) => {
          if (choiceLabel != null) {
            return asset.type === key && asset.choiceLabel === choiceLabel;
          }
          if (key) {
            return asset.type === key;
          }
          return true;
        }) || [];

      // Find the minimum unoccupied index in the assets array
      let index = 0;
      if (assets && assets.length > 0) {
        const usedIndexes = new Set(assets.map((a) => a.index ?? 0));
        while (usedIndexes.has(index)) {
          index++;
        }
      }

      const column = assetFieldMapping[key];

      let field: keyof IAssetSchema;
      if (choiceLabel != null) {
        const choiceIdx = choiceLabel.toLowerCase().charCodeAt(0) - 97;
        const fieldNameMap: Partial<Record<ELightGalleryType, string>> = {
          [ELightGalleryType.CHOICE]: 'name',
          [ELightGalleryType.CHOICE_EXPLANATION]: 'explanation',
        };
        const fieldName = fieldNameMap[key] ?? 'name';
        field = `${column}.${choiceIdx}.${fieldName}` as keyof IAssetSchema;
      } else {
        field = column as keyof IAssetSchema;
      }

      if (type === 'job') {
        await createAssetForJob(result, field, index, latestAssets);
      } else {
        await createAssetForNormal(result, field, index, latestAssets);
      }
    },
    [setValue, assetEditor],
  );

  if (!assetEditor) {
    return null;
  }

  return (
    <AssetEditor
      title={[
        ELightGalleryType[assetEditor.key]?.toUpperCase(),
        assetEditor.choiceLabel ? `- ${assetEditor.choiceLabel.toUpperCase()}` : null,
        'Editor',
      ]
        .filter(Boolean)
        .join(' ')}
      isOpen={!!assetEditor}
      onClose={closeAssetEditor}
      onSave={handleSave}
      loading={createAssetsLoading}
      isDisabled={isDisabled}
    />
  );
};
